import { checkSyncEnabled, checkVisualizeEnabled, useMe } from '@salescore/client-common'
import { wrapKeyboardEvent } from '@salescore/frontend-common'
import { isNotNullish } from 'effect/Predicate'
import { useCallback, useState } from 'react'

import { useLauncherCommands } from './useLauncherCommands'
import type { LauncherCommandType, LauncherCommandV2 } from './useMoveCommands.tsx'

const MAX_VISIBLE_COMMANDS_NUM = 60
export const useLauncherState = (pickView: (id: string) => void) => {
  const me = useMe()
  const isSyncEnabled = checkSyncEnabled(me.myUser)
  const isVisualizeEnabled = checkVisualizeEnabled(me.myUser)

  const [filterType, setFilterType] = useState<LauncherCommandType>('all')

  const moveNextFilter = useCallback(
    (option?: { reverse?: boolean }) => {
      const filtersRaw: Array<LauncherCommandType | undefined> = [
        'all',
        isSyncEnabled || isVisualizeEnabled ? `sheet` : undefined,
        isVisualizeEnabled ? `kpi` : undefined,
        isVisualizeEnabled ? `kpiPivot` : undefined,
        'page',
        'archived',
      ]
      const filters = filtersRaw.filter(isNotNullish)
      const currentFilterIndex = filters.indexOf(filterType)
      const d = option?.reverse === true ? filters.length - 1 : 1
      const nextFilter = filters[(currentFilterIndex + d) % filters.length] ?? `all`
      setFilterType(nextFilter)
    },
    [isSyncEnabled, isVisualizeEnabled, filterType, setFilterType],
  )
  const [searchKey, setSearchKey] = useState('')
  const setSearchKeyAndResetCursor = (searchKey: string): void => {
    setSearchKey(searchKey)
    setCursor(0) // 検索ワードの変更時はカーソルを先頭にする
  }
  const [cursor, setCursor] = useState(0)
  const commands = useLauncherCommands({
    pickView,
  })
  const lowerCaseSearchKeys = searchKey.toLowerCase().split(/\s/v)
  const filteredCommands = commands
    .filter((x) => lowerCaseSearchKeys.every((key) => [x.searchKeyword ?? '', x.title].join(' ').includes(key)))
    .filter((x) => (filterType === 'all' && x.type !== 'archived') || x.type === filterType) // typeが一致するか、allのとき(allの時にarchivedは非表示)
    // .mySortBy((x) => Math.max(x.title.indexOf(lowerCaseSearchKey), x.searchKeyword?.indexOf(lowerCaseSearchKey) ?? -1))
    .slice(0, MAX_VISIBLE_COMMANDS_NUM) // あまり増やしすぎると描画が重い？どれくらいが適正か悩ましい。

  const executeCommand = (command: LauncherCommandV2 | undefined, closeLauncher: () => void): void => {
    if (command === undefined) {
      return
    }

    void command.method()
    if (command.options?.hideModalAfterExecution !== false) {
      closeLauncher()
    }
  }

  const moveDown = (): void => {
    setCursor((x) => {
      // 最下段にいるとき
      if (x === filteredCommands.length - 1) {
        return 0
      }
      return x + 1
    })
  }
  const moveUp = (): void => {
    setCursor((x) => {
      // 最上段にいるとき
      if (x === 0) {
        return filteredCommands.length - 1
      }
      return x - 1
    })
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, closeLauncher: () => void): void => {
    const wrapped = wrapKeyboardEvent(event)
    switch (event.key) {
      case 'Enter': {
        const selectedCommand = filteredCommands[cursor]
        executeCommand(selectedCommand, closeLauncher)
        event.preventDefault()
        event.stopPropagation()
        return
      }
      case 'ArrowDown': {
        moveDown()
        event.preventDefault()
        event.stopPropagation()
        return
      }
      case 'ArrowUp': {
        moveUp()
        event.preventDefault()
        event.stopPropagation()
        return
      }
      case 'Tab': {
        if (event.shiftKey) {
          moveNextFilter({ reverse: true })
        } else {
          moveNextFilter()
        }
        event.preventDefault()
        event.stopPropagation()
        return
      }
    }

    if (wrapped.macCtrlKey) {
      switch (event.key) {
        case 'n': {
          moveDown()
          event.preventDefault()
          event.stopPropagation()
          return
        }
        case 'p': {
          moveUp()
          event.preventDefault()
          event.stopPropagation()
          return
        }
      }
    }

    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: あまり厳密に判定していない
    if (wrapped.commandKey) {
      const metaCommands = filteredCommands.filter((x) => x.shortcut?.includes('command') === true)
      const selectedCommand = metaCommands.find((x) => x.shortcut?.includes(event.key) === true)
      if (selectedCommand !== undefined) {
        executeCommand(selectedCommand, closeLauncher)
        event.preventDefault()
        event.stopPropagation()
      }
    }
  }

  return {
    filterType,
    setFilterType,
    searchKey,
    filteredCommands,
    // コマンドの表示数の上限を超えて、かつカーソルが下の方にあれば、警告を表示する
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: 表示側で仮想化を行い、無限に表示できるようにするべき
    maxVisibleCommandExceededWarningVisibility:
      filteredCommands.length === MAX_VISIBLE_COMMANDS_NUM && cursor > MAX_VISIBLE_COMMANDS_NUM - 5,
    cursor,
    setSearchKeyAndResetCursor,
    handleKeyDown,
    executeCommand,
    setCursor,
  }
}
